import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getSingleReservation,
  selectSingleReservation,
} from '../redux/feature/reservation/slice';
import { useParams } from 'react-router-dom';
import { Card, Table, Typography, Progress, Spin } from 'antd';
import { formatedPrice } from '../utils/helper';
import { Icon } from '@iconify/react';
import AddCard from '../components/addCard';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';

const { Text } = Typography;

function TripPayment() {
  const dispatch = useDispatch();
  const { tripId } = useParams();
  const trip = useSelector(selectSingleReservation);
  const [loader, setLoader] = useState(true);
  const [stripepromise, setStripePromise] = useState();

  const remainingAmount = (trip?.total || 0) - (trip?.paid || 0);
  const progressPercentage =
    trip?.total > 0 ? Math.round(((trip?.paid || 0) / trip.total) * 100) : 0;

  const connectedAccId =
    trip?.tripAccociateCompanyId?.stripeAccountType === 'oauth'
      ? trip?.tripAccociateCompanyId?.stripeUserId
      : trip?.tripAccociateCompanyId?.stripeId;

  useEffect(() => {
    if (connectedAccId) {
      const stripeKey = process.env.REACT_APP_PUBLIC_STRIPE_KEY;
      const promise = loadStripe(stripeKey, {
        stripeAccount: connectedAccId,
      });
      setStripePromise(promise);
    }
  }, [connectedAccId]);

  useEffect(() => {
    if (tripId) {
      dispatch(getSingleReservation({ id: tripId, isTrip: true }));
    }
    setLoader(false);
  }, [dispatch, tripId]);

  const allTransactions = [];
  (trip?.payments || []).forEach((payment) => {
    // 1. Add the main payment transaction
    allTransactions.push({
      _id: payment._id || payment.id,
      type: 'payment',
      amount: payment.amount,
      method: payment.method,
      status: payment.status,
      date: payment.paymentDate || payment.createdAt,
      paymentDate: payment.paymentDate || payment.createdAt, // fallback
      rawPayment: payment,
    });

    // 2. Add detailed refunds as separate transaction rows
    if (payment.refunds && payment.refunds.length > 0) {
      payment.refunds.forEach((refund) => {
        allTransactions.push({
          _id: refund._id || refund.id,
          type: 'refund',
          amount: refund.amount,
          method: payment.method,
          status: 'refunded',
          date: refund.createdAt,
          paymentDate: refund.createdAt, // fallback
          reason: refund.reason || 'Manual refund',
          parentPaymentId: payment.id || payment._id,
        });
      });
    } else if (payment.refundedAmount > 0) {
      // Add legacy refund
      allTransactions.push({
        _id: `legacy-${payment.id || payment._id}`,
        type: 'refund',
        amount: payment.refundedAmount,
        method: payment.method,
        status: 'refunded',
        date: payment.updatedAt || payment.paymentDate || payment.createdAt,
        paymentDate: payment.updatedAt || payment.paymentDate || payment.createdAt,
        reason: 'Legacy refund data recorded before detailed tracking',
        parentPaymentId: payment.id || payment._id,
      });
    }
  });

  // Sort by date descending
  allTransactions.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  const columns = [
    {
      title: 'Payment Date',
      dataIndex: 'paymentDate',
      key: 'paymentDate',
      width: 150,
      render: (text, record) => {
        let dateStr = '—';
        if (text) {
          try {
            dateStr = new Date(text).toLocaleDateString();
          } catch (e) {
            dateStr = String(text);
          }
        }
        return (
          <div>
            <div>{dateStr}</div>
            {record.type === 'refund' && record.reason && (
              <div className="text-[10px] text-gray-400 italic font-medium mt-0.5">{record.reason}</div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      width: 150,
      render: (text, record) => {
        if (record.type === 'refund') {
          return <span style={{ color: '#DC2626', fontWeight: 'bold' }}>-{formatedPrice(text)}</span>;
        }
        return <span style={{ color: '#16A34A', fontWeight: 'bold' }}>{formatedPrice(text)}</span>;
      },
    },
    {
      title: 'Method',
      dataIndex: 'method',
      key: 'method',
      width: 150,
      render: (text) => {
        const method = (text || '').toLowerCase();
        let displayText = 'Cash', bgColor = '#DCFCE7', textColor = '#16A34A', icon = 'mdi:cash-check';
        if (method === 'card') {
          displayText = 'Card';
          bgColor = '#DCFCE7';
          textColor = '#16A34A';
          icon = 'majesticons:creditcard';
        } else if (method === 'bank_transfer') {
          displayText = 'Bank';
          bgColor = '#E0F2FE';
          textColor = '#0369A1';
          icon = 'mdi:bank';
        } else if (method === 'other') {
          displayText = 'Other';
          bgColor = '#F3F4F6';
          textColor = '#4B5563';
          icon = 'mdi:help-circle-outline';
        }
        return (
          <div
            className="inline-flex items-center cursor-pointer rounded-lg hover:scale-105 font-normal whitespace-nowrap capitalize transition-transform duration-300 ease-in-out px-3 py-2"
            style={{ backgroundColor: bgColor }}
          >
            <Icon icon={icon} className="w-4 h-4 mr-1" style={{ color: textColor }} />
            <span className="text-xs font-semibold" style={{ color: textColor }}>
              {displayText}
            </span>
          </div>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 150,
      render: (text) => {
        const status = (text || '').toLowerCase();
        let displayText = status;
        let bgColor = '#FEF9C3', textColor = '#D49F27', icon = 'mdi:timer-sand'; // default pending
        
        if (status === 'completed' || status === 'paid') {
          displayText = 'Completed';
          bgColor = '#DCFCE7';
          textColor = '#16A34A';
          icon = 'mdi:check-circle-outline';
        } else if (status === 'refunded' || status === 'fully_refunded') {
          displayText = 'Refunded';
          bgColor = '#FEE2E2';
          textColor = '#DC2626';
          icon = 'mdi:alert-circle-outline';
        } else if (status === 'partial_refunded') {
          displayText = 'Partial Refund';
          bgColor = '#FEF9C3';
          textColor = '#D49F27';
          icon = 'mdi:alert-outline';
        } else if (status === 'failed') {
          displayText = 'Failed';
          bgColor = '#FEE2E2';
          textColor = '#DC2626';
          icon = 'mdi:close-circle-outline';
        }

        return (
          <div
            className="inline-flex items-center cursor-pointer rounded-lg hover:scale-105 font-normal whitespace-nowrap capitalize transition-transform duration-300 ease-in-out px-3 py-2"
            style={{ backgroundColor: bgColor }}
          >
            <Icon icon={icon} className="w-4 h-4 mr-1" style={{ color: textColor }} />
            <span className="text-xs font-semibold" style={{ color: textColor }}>
              {displayText}
            </span>
          </div>
        );
      },
    },
  ];

  return (
    <div className="checkout-page bg-gray-100 min-h-screen p-6" style={{ zoom: '0.8' }}>
      {loader || !trip?._id ? (
        <div className="flex justify-center items-center h-screen">
          <Spin size="large" />
        </div>
      ) : (
        <div className="checkout-content md:max-w-4xl mx-auto space-y-6">
          <Card
            className="shadow-lg"
            headStyle={{ backgroundColor: '#058CBD', color: 'white' }}
            title="Trip Details"
          >
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <Text className="text-gray-600">Trip ID:</Text>
                <Text strong>{trip.tripId}</Text>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <Text className="text-gray-600">Total Amount:</Text>
                  <Text strong className="text-green-600">
                    {formatedPrice(trip.total)}
                  </Text>
                </div>
                <div className="flex justify-between items-center">
                  <Text className="text-gray-600">Paid Amount:</Text>
                  <Text strong className="text-[#058CBD]">
                    {formatedPrice(trip.paid)}
                  </Text>
                </div>
                <div className="flex justify-between items-center">
                  <Text className="text-gray-600">Remaining:</Text>
                  <Text strong className="text-red-600">
                    {formatedPrice(remainingAmount)}
                  </Text>
                </div>
              </div>
              <div className="mt-4">
                <Text className="text-sm text-gray-500 block mb-1">
                  Payment Progress
                </Text>
                <Progress
                  percent={progressPercentage}
                  status="active"
                  strokeColor={{
                    '0%': '#058CBD',
                    '100%': '#87d068',
                  }}
                />
              </div>
            </div>
          </Card>

          <Card
            className="shadow-lg"
            headStyle={{ backgroundColor: '#058CBD', color: 'white' }}
            title="Previous Payments"
          >
            <div className="overflow-x-auto">
              <Table
                columns={columns}
                dataSource={allTransactions}
                rowKey="_id"
                pagination={{
                  pageSize: 5,
                  showQuickJumper: true,
                  showTotal: (total, range) =>
                    `${range[0]}-${range[1]} of ${total} items`,
                  className: 'pagination-control',
                }}
              />
            </div>
          </Card>

          <Card className="shadow-lg" title="Make a Payment">
            {stripepromise && (
              <Elements stripe={stripepromise}>
                <AddCard
                  tripId={tripId}
                  ConnAccId={connectedAccId}
                  trip={true}
                  remainingAmount={remainingAmount}
                />
              </Elements>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}

export default TripPayment;
